'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { activeVehicles, activeVehicleTypes, locations, Vehicle, vehicles } from '@/data/vehicles';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Shield,
  Bike,
  Heart,
  Settings,
  Phone,
  Gauge,
  Battery,
  CloudRain,
  Waves,
  Wifi,
  Loader2,
  CheckCircle2,
  ArrowLeft,
  Calendar
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { trackEvent, trackMeasurement, getDurationTier } from '@/lib/analytics';
import { appSettings } from '@/app-settings';


// Personal Info Interface
interface PersonalInfo {
  firstName: string;
  lastName: string;
  birthYear: number;
  phone: string;
  email: string;
  address: string;
  city: string;
  country: string;
  zipCode: string;
}

type BookingStep = 'selection' | 'confirmation' | 'processing' | 'success';

interface BookingFormProps {
  selectedVehicle?: string;
  onVehicleChange?: (vehicleId: string) => void;
  className?: string;
}

// Extras available for booking
const extras = [
  { id: 'power-bank', name: 'Power Bank', price: 1, icon: Battery },
  { id: 'raincoat', name: 'Raincoat', price: 1, icon: CloudRain },
  { id: 'surf-rack', name: 'Surf Rack', price: 0.5, icon: Waves },
  { id: 'pocket-wifi', name: 'Pocket WiFi 4G', price: 2, icon: Wifi },
];

// ... (existing FeatureIcon component)

// ... (existing BookingConfirmation component)

// Vehicle Slider Picker Component
function VehicleSliderPicker({
  value,
  onChange
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  const [activeFilter, setActiveFilter] = useState<string>(() => {
    // Initialize filter based on the selected vehicle's type
    if (value) {
      const selectedVehicle = vehicles.find(v => v.id === value);
      if (selectedVehicle) {
        return 'all'; // Keep 'all' so the vehicle is visible
      }
    }
    return 'all';
  });

  const [currentIndex, setCurrentIndex] = useState(() => {
    // Initialize index based on the value prop
    if (value) {
      const index = vehicles.findIndex(v => v.id === value);
      return index >= 0 ? index : 0;
    }
    return 0;
  });

  const vehicleTypes = ['all', ...activeVehicleTypes];

  const filteredVehicles = activeFilter === 'all'
    ? activeVehicles
    : activeVehicles.filter(v => v.type === activeFilter);

  const currentVehicle = filteredVehicles[currentIndex];

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? filteredVehicles.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === filteredVehicles.length - 1 ? 0 : prev + 1));
  };

  // Sync currentIndex when value changes from outside (e.g., query param)
  useEffect(() => {
    if (value) {
      const index = filteredVehicles.findIndex(v => v.id === value);
      if (index >= 0 && index !== currentIndex) {
        setCurrentIndex(index);
      }
    }
  }, [value, filteredVehicles]);

  // Reset index when filter changes, but try to keep selected vehicle visible
  useEffect(() => {
    if (value) {
      const index = filteredVehicles.findIndex(v => v.id === value);
      if (index >= 0) {
        setCurrentIndex(index);
      } else {
        setCurrentIndex(0);
      }
    } else {
      setCurrentIndex(0);
    }
  }, [activeFilter]);

  // Auto-select current vehicle when navigating
  useEffect(() => {
    if (currentVehicle && currentVehicle.id !== value) {
      onChange(currentVehicle.id);
    }

    if (currentVehicle) {
      trackEvent('booking_view_item', {
        vehicle_category: currentVehicle.type,
        vehicle_model: currentVehicle.name,
      });
    }
  }, [currentIndex, currentVehicle]);

  return (
    <div className="space-y-4">
      {/* Type Filter */}
      <div className="flex gap-2 flex-wrap pb-2">
        {vehicleTypes.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => setActiveFilter(type)}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors",
              activeFilter === type
                ? "bg-primary text-primary-foreground"
                : "bg-white/[0.06] text-muted-foreground hover:bg-white/[0.1] hover:text-foreground"
            )}
          >
            {type === 'all' ? 'All Vehicles' : `${type}s`}
          </button>
        ))}
      </div>

      {/* Single Vehicle Carousel */}
      <div className="relative">
        {currentVehicle && (
          <div
            className={cn(
              "w-full rounded-xl border-2 transition-all duration-300 overflow-hidden bg-gradient-to-b from-white/[0.08] to-white/[0.02]",
              value === currentVehicle.id
                ? "border-primary shadow-lg shadow-primary/20"
                : "border-white/[0.1]"
            )}
          >
            {/* Large Vehicle Image */}
            <div className="relative h-[440px] bg-gradient-to-b from-muted/20 to-transparent">
              <Image
                src={currentVehicle.image}
                alt={currentVehicle.name}
                fill
                className="object-contain p-8 transition-transform duration-300"
              />
              {value === currentVehicle.id && (
                <div className="absolute top-4 right-4 h-8 w-8 rounded-full bg-primary flex items-center justify-center shadow-lg">
                  <Check className="h-5 w-5 text-white" />
                </div>
              )}
              {/* Vehicle Counter */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/40 backdrop-blur-sm text-xs text-white/80">
                {currentIndex + 1} / {filteredVehicles.length}
              </div>
            </div>

            {/* Vehicle Info */}
            <div className="p-5 space-y-3 border-t border-white/[0.06]">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold">{currentVehicle.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {currentVehicle.type} {currentVehicle.cc ? `• ${currentVehicle.cc} CC` : ''} • {currentVehicle.features.transmission}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-primary">${currentVehicle.price}</p>
                  <p className="text-xs text-muted-foreground">per day</p>
                </div>
              </div>

              {/* Quick Features */}
              <div className="flex gap-2 flex-wrap">
                {currentVehicle.features.transmission && (
                  <span className="px-2 py-1 text-xs rounded-md bg-white/[0.06] text-muted-foreground">
                    {currentVehicle.features.transmission}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Navigation Arrows */}
        <button
          type="button"
          onClick={goToPrevious}
          className="absolute left-0 top-[220px] -translate-x-1/2 h-12 w-12 rounded-full bg-card border border-white/[0.15] flex items-center justify-center hover:bg-primary hover:text-black hover:border-primary transition-all z-10 shadow-lg"
          aria-label="Previous vehicle"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          type="button"
          onClick={goToNext}
          className="absolute right-0 top-[220px] translate-x-1/2 h-12 w-12 rounded-full bg-card border border-white/[0.15] flex items-center justify-center hover:bg-primary hover:text-black hover:border-primary transition-all z-10 shadow-lg"
          aria-label="Next vehicle"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {/* Dot Indicators */}
      <div className="flex justify-center gap-1.5 pt-2">
        {filteredVehicles.map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => setCurrentIndex(index)}
            className={cn(
              "h-2 rounded-full transition-all duration-300",
              index === currentIndex
                ? "w-6 bg-primary"
                : "w-2 bg-white/[0.2] hover:bg-white/[0.4]"
            )}
            aria-label={`Go to vehicle ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

// Vehicle Feature Icon Component
function FeatureIcon({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string | boolean }) {
  if (value === false) return null;
  return (
    <div className="flex items-center gap-2 text-sm">
      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div>
        <span className="text-muted-foreground text-xs uppercase">{label}:</span>
        <span className="text-foreground font-medium ml-1">
          {typeof value === 'boolean' ? 'Yes' : value}
        </span>
      </div>
    </div>
  );
}

// Booking Confirmation View
function BookingConfirmation({
  vehicle,
  pickupDate,
  dropoffDate,
  selectedExtras,
  onExtrasChange,
  personalInfo,
  onPersonalInfoChange,
  onSubmit,
  onBack,
  isSubmitting,
}: {
  vehicle: Vehicle;
  pickupDate: Date;
  dropoffDate: Date;
  selectedExtras: string[];
  onExtrasChange: (extras: string[]) => void;
  personalInfo: PersonalInfo;
  onPersonalInfoChange: (info: PersonalInfo) => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}) {
  const days = Math.ceil((dropoffDate.getTime() - pickupDate.getTime()) / (1000 * 60 * 60 * 24));
  const vehicleTotal = vehicle.price * days;
  const extrasTotal = selectedExtras.reduce((sum, extraId) => {
    const extra = extras.find(e => e.id === extraId);
    return sum + (extra ? extra.price * days : 0);
  }, 0);
  const total = vehicleTotal + extrasTotal;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const toggleExtra = (extraId: string) => {
    if (selectedExtras.includes(extraId)) {
      onExtrasChange(selectedExtras.filter(id => id !== extraId));
    } else {
      onExtrasChange([...selectedExtras, extraId]);
    }
  };

  const handleInputChange = (field: keyof PersonalInfo, value: string | number) => {
    onPersonalInfoChange({ ...personalInfo, [field]: value });
  };

  const currentYear = new Date().getFullYear();
  const isFormValid = personalInfo.firstName && personalInfo.lastName && personalInfo.birthYear && (currentYear - personalInfo.birthYear) >= 21 && personalInfo.phone && personalInfo.email && personalInfo.city && personalInfo.country;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-display font-bold tracking-tight">Your Booking</h1>
        <p className="text-muted-foreground mt-2">Please, check your booking</p>
      </div>

      {/* Vehicle Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Vehicle Info */}
            <div className="flex-1">
              <span className="text-xs uppercase text-primary font-semibold tracking-wider">
                {vehicle.type}
              </span>
              <h2 className="text-2xl font-bold mt-1">
                {vehicle.name} {vehicle.cc && `${vehicle.cc} CC`}
              </h2>
              <p className="text-xl font-semibold text-foreground mt-1">
                ${vehicle.price} <span className="text-sm font-normal text-muted-foreground">/ day</span>
              </p>

              {/* Date Range */}
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="text-primary font-medium">{formatDate(pickupDate)}</span>
                <span>—</span>
                <span className="text-primary font-medium">{formatDate(dropoffDate)}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {days} day{days > 1 ? 's' : ''} rental
              </p>
            </div>

            {/* Vehicle Image */}
            <div className="relative h-48 md:h-auto md:w-64 flex-shrink-0">
              <Image
                src={vehicle.image}
                alt={vehicle.name}
                fill
                className="object-contain"
              />
            </div>
          </div>

          {/* Features */}
          <div className="mt-6 pt-6 border-t border-border grid grid-cols-2 md:grid-cols-3 gap-4">
            <FeatureIcon icon={Shield} label="Full Insurance" value={vehicle.features.fullInsurance} />
            {vehicle.features.helmets && (
              <FeatureIcon icon={Bike} label="Helmets" value={vehicle.features.helmets} />
            )}
            <FeatureIcon icon={Heart} label="First-Aid Kit" value={vehicle.features.firstAidKit} />
            <FeatureIcon icon={Settings} label="Transmission" value={vehicle.features.transmission} />
            <FeatureIcon icon={Phone} label="24/7 Road Side Assistance" value={vehicle.features.roadAssistance} />
            <FeatureIcon icon={Gauge} label="Mileage" value={vehicle.features.mileage} />
          </div>
        </CardContent>
      </Card>

      {/* Extras & Total Card */}
      <Card>
        <CardContent className="p-6">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Extras */}
            <div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-4">Extras</h3>
              <div className="space-y-2">
                {extras.map((extra) => (
                  <label
                    key={extra.id}
                    className="flex items-center p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] cursor-pointer transition-colors border border-transparent hover:border-white/[0.1]"
                  >
                    <input
                      type="checkbox"
                      checked={selectedExtras.includes(extra.id)}
                      onChange={() => toggleExtra(extra.id)}
                      className="h-4 w-4 shrink-0 rounded border-white/20 bg-white/10 text-primary focus:ring-primary focus:ring-offset-0"
                    />
                    <extra.icon className="h-4 w-4 text-primary shrink-0 ml-3" />
                    <span className="text-sm font-medium ml-2">{extra.name}</span>
                    <span className="text-sm text-primary font-semibold ml-auto shrink-0">${extra.price}</span>
                    <span className="text-sm text-muted-foreground ml-1 shrink-0">/ day</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Price Summary */}
            <div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-4">Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Vehicle ({days} days)</span>
                  <span>${vehicleTotal}</span>
                </div>
                {extrasTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Extras ({days} days)</span>
                    <span>${extrasTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold pt-4 border-t border-border">
                  <span>Total</span>
                  <span className="text-primary">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Information */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-xl font-bold uppercase tracking-wide mb-6">Personal Information</h3>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                First Name <span className="text-primary">*</span>
              </Label>
              <Input
                placeholder="Enter your first name"
                value={personalInfo.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                data-testid="first-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                Last Name <span className="text-primary">*</span>
              </Label>
              <Input
                placeholder="Enter your last name"
                value={personalInfo.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                data-testid="last-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                Born In <span className="text-primary">*</span>
              </Label>
              <Select
                value={personalInfo.birthYear ? personalInfo.birthYear.toString() : ''}
                onValueChange={(value) => handleInputChange('birthYear', parseInt(value))}
              >
                <SelectTrigger className="h-12" data-testid="birth-year">
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: new Date().getFullYear() - 21 - 1940 + 1 }, (_, i) => {
                    const year = new Date().getFullYear() - 21 - i;
                    return (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                Phone Number <span className="text-primary">*</span>
              </Label>
              <Input
                type="tel"
                placeholder="Enter your phone number"
                value={personalInfo.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                data-testid="phone"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                Email <span className="text-primary">*</span>
              </Label>
              <Input
                type="email"
                placeholder="Enter your email address"
                value={personalInfo.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                data-testid="email"
              />
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <Label className="text-xs font-semibold uppercase text-muted-foreground">
              Address
            </Label>
            <Input
              placeholder="Enter your address"
              value={personalInfo.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-6">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                City <span className="text-primary">*</span>
              </Label>
              <Input
                placeholder="Enter your city"
                value={personalInfo.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                data-testid="city"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                Country <span className="text-primary">*</span>
              </Label>
              <Input
                placeholder="Enter your country"
                value={personalInfo.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                data-testid="country"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                Zip Code
              </Label>
              <Input
                placeholder="Enter your zip code"
                value={personalInfo.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          variant="outline"
          size="lg"
          onClick={onBack}
          disabled={isSubmitting}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Selection
        </Button>
        <Button
          size="lg"
          onClick={onSubmit}
          disabled={!isFormValid || isSubmitting}
          className="min-w-[200px]"
          data-testid="book-now-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Processing...
            </>
          ) : (
            'Book Now'
          )}
        </Button>
      </div>
    </div>
  );
}

// Success State Component
function BookingSuccess({
  vehicle,
  pickupDate,
  dropoffDate,
  personalInfo,
  onNewBooking,
  bookingRef
}: {
  vehicle: Vehicle;
  pickupDate: Date;
  dropoffDate: Date;
  personalInfo: PersonalInfo;
  onNewBooking: () => void;
  bookingRef: string;
}) {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="text-center py-12 space-y-8">
      {/* Success Icon */}
      <div className="relative mx-auto w-24 h-24">
        <div className="absolute inset-0 bg-green-500/20 rounded-full" />
        <div className="relative w-24 h-24 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30">
          <CheckCircle2 className="h-12 w-12 text-white" />
        </div>
      </div>

      {/* Success Message */}
      <div>
        <h1 className="text-4xl font-display font-bold text-foreground mb-2">
          Booking Confirmed!
        </h1>
        <p className="text-lg text-muted-foreground">
          Thank you, {personalInfo.firstName}! Your adventure awaits.
        </p>
      </div>

      {/* Booking Reference */}
      <div className="inline-block bg-card border border-border rounded-xl p-6">
        <p className="text-sm text-muted-foreground mb-1">Booking Reference</p>
        <p className="text-2xl font-mono font-bold text-primary">{bookingRef}</p>
      </div>

      {/* Booking Summary Card */}
      <Card className="max-w-2xl mx-auto">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-6 items-center">
            {/* Vehicle Image */}
            <div className="relative h-32 w-48 flex-shrink-0">
              <Image
                src={vehicle.image}
                alt={vehicle.name}
                fill
                className="object-contain"
              />
            </div>

            {/* Details */}
            <div className="text-left flex-1 space-y-2">
              <div>
                <span className="text-xs uppercase text-primary font-semibold">{vehicle.type}</span>
                <h3 className="text-xl font-bold">{vehicle.name} {vehicle.cc && `${vehicle.cc} CC`}</h3>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar className="h-4 w-4 text-primary" />
                <span>{formatDate(pickupDate)} — {formatDate(dropoffDate)}</span>
              </div>
              <p className="text-lg font-semibold text-primary">${vehicle.price}/day</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* What's Next */}
      <div className="max-w-xl mx-auto space-y-4 text-left bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-lg">What&apos;s Next?</h3>
        <ul className="space-y-3 text-muted-foreground">
          <li className="flex items-start gap-3">
            <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-primary">1</span>
            </div>
            <span>Check your email for a confirmation with all booking details</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-primary">2</span>
            </div>
            <span>We&apos;ll contact you via WhatsApp to confirm pickup details</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-primary">3</span>
            </div>
            <span>Bring your passport, driving license, and this booking reference</span>
          </li>
        </ul>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
        <Button variant="outline" size="lg" onClick={onNewBooking}>
          Make Another Booking
        </Button>
        <Button size="lg" asChild>
          <a
            href={`https://wa.me/${appSettings.contactPhoneFull}?text=Hi! I just made a booking (Ref: ${bookingRef}) for ${vehicle.name}. Looking forward to my trip!`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Contact Us on WhatsApp
          </a>
        </Button>
      </div>
    </div>
  );
}

export default function BookingForm({
  selectedVehicle = '',
  onVehicleChange,
  className
}: BookingFormProps) {
  const [step, setStep] = useState<BookingStep>('selection');
  const [bookingRef, setBookingRef] = useState('');
  const [vehicle, setVehicle] = useState(selectedVehicle);
  const [pickupLocation, setPickupLocation] = useState('negombo-hq');
  const [dropoffLocation, setDropoffLocation] = useState('negombo-hq');
  const [pickupDate, setPickupDate] = useState<Date>();
  const [dropoffDate, setDropoffDate] = useState<Date>();
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    firstName: '',
    lastName: '',
    birthYear: 0,
    phone: '',
    email: '',
    address: '',
    city: '',
    country: '',
    zipCode: '',
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // Sync with prop
  useEffect(() => {
    if (selectedVehicle) {
      setVehicle(selectedVehicle);
    }
  }, [selectedVehicle]);

  const handleVehicleChange = (value: string) => {
    setVehicle(value);
    onVehicleChange?.(value);
  };

  const selectedVehicleData = vehicles.find(v => v.id === vehicle);
  const canProceed = vehicle && pickupDate && dropoffDate && dropoffDate > pickupDate;

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleContinue = () => {
    if (canProceed) {
      if (selectedVehicleData && pickupDate && dropoffDate) {
        const days = Math.ceil((dropoffDate.getTime() - pickupDate.getTime()) / (1000 * 60 * 60 * 24));
        const durationTier = getDurationTier(days);

        trackEvent('booking_initiate', {
          vehicle_category: selectedVehicleData.type,
          duration_tier: durationTier
        });
      }
      setStep('confirmation');
      scrollToTop();
    }
  };

  const handleBack = () => {
    setStep('selection');
    scrollToTop();
  };

  const handleSubmit = async () => {
    setStep('processing');
    scrollToTop();

    if (selectedVehicleData) {
      trackEvent('booking_submit', {
        vehicle_category: selectedVehicleData.type,
        has_extras: selectedExtras.length > 0 ? 'true' : 'false',
        extras_chosen: selectedExtras.join(','),
      });
    }

    try {
      const response = await fetch('/api/book', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          vehicle,
          pickupDate: pickupDate?.toISOString(),
          dropoffDate: dropoffDate?.toISOString(),
          personalInfo,
          selectedExtras,
        }),
      });

      const result = await response.json();

      // Artificial delay
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (response.ok) {
        if (selectedVehicleData && pickupDate && dropoffDate) {
          const days = Math.ceil((dropoffDate.getTime() - pickupDate.getTime()) / (1000 * 60 * 60 * 24));

          // Calculate total value
          const vehicleTotal = selectedVehicleData.price * days;
          const extrasTotal = selectedExtras.reduce((sum, extraId) => {
            const extra = extras.find(e => e.id === extraId);
            return sum + (extra ? extra.price * days : 0);
          }, 0);
          const totalValue = vehicleTotal + extrasTotal;

          trackEvent('booking_success', {
            vehicle_category: selectedVehicleData.type,
            user_country: personalInfo.country,
            birth_year: personalInfo.birthYear.toString(),
            extras_chosen: selectedExtras.join(','),
          });

          trackMeasurement('booking_value', totalValue, {
            vehicle_category: selectedVehicleData.type,
          });
        }
        setBookingRef(result.bookingRef);
        setStep('success');
      } else {
        trackEvent('booking_error', {
          error_type: 'api_error',
        });
        alert('Something went wrong. Please try again.');
        setStep('selection');
      }
    } catch (error) {
      trackEvent('booking_error', {
        error_type: 'network_error',
      });
      console.error('Booking failed:', error);
      alert('Something went wrong. Please try again.');
      setStep('selection');
    }
  };


  const handleNewBooking = () => {
    setStep('selection');
    setVehicle('');
    setPickupDate(undefined);
    setDropoffDate(undefined);
    setSelectedExtras([]);
    setPersonalInfo({
      firstName: '',
      lastName: '',
      birthYear: 2000,
      phone: '',
      email: '',
      address: '',
      city: '',
      country: '',
      zipCode: '',
    });
  };

  // Processing State
  if (step === 'processing') {
    return (
      <div ref={containerRef} className={cn("py-20", className)}>
        <div className="text-center space-y-8">
          {/* Loading Animation */}
          <div className="relative mx-auto w-24 h-24">
            <div className="absolute inset-0 border-4 border-primary/20 rounded-full" />
            <div className="absolute inset-0 border-4 border-transparent border-t-primary rounded-full animate-spin" />
            <div className="absolute inset-4 bg-card rounded-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-primary animate-spin" />
            </div>
          </div>

          {/* Loading Text */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Processing Your Booking</h2>
            <p className="text-muted-foreground">Please wait while we confirm your reservation...</p>
          </div>

          {/* Progress Steps */}
          <div className="max-w-md mx-auto space-y-3">
            {[
              { label: 'Verifying vehicle availability', delay: 0 },
              { label: 'Processing your details', delay: 3500 },
              { label: 'Confirming reservation', delay: 7000 },
            ].map((item, i) => (
              <ProcessingStep key={i} label={item.label} delay={item.delay} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Success State
  if (step === 'success' && selectedVehicleData && pickupDate && dropoffDate) {
    return (
      <div ref={containerRef} className={className}>
        <BookingSuccess
          vehicle={selectedVehicleData}
          pickupDate={pickupDate}
          dropoffDate={dropoffDate}
          personalInfo={personalInfo}
          onNewBooking={handleNewBooking}
          bookingRef={bookingRef}
        />
      </div>
    );
  }

  // Confirmation State
  if (step === 'confirmation' && selectedVehicleData && pickupDate && dropoffDate) {
    return (
      <div ref={containerRef} className={className}>
        <BookingConfirmation
          vehicle={selectedVehicleData}
          pickupDate={pickupDate}
          dropoffDate={dropoffDate}
          selectedExtras={selectedExtras}
          onExtrasChange={setSelectedExtras}
          personalInfo={personalInfo}
          onPersonalInfoChange={setPersonalInfo}
          onSubmit={handleSubmit}
          onBack={handleBack}
          isSubmitting={false}
        />
      </div>
    );
  }

  // Selection State (default)
  return (
    <Card ref={containerRef} className={className}>
      <CardHeader className="text-center pb-4">
        <CardTitle className="text-3xl">Book Your Ride</CardTitle>
        <p className="text-muted-foreground">Select your vehicle and dates to get started</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => { e.preventDefault(); handleContinue(); }} className="space-y-8">
          {/* Vehicle Selection - Slider Picker */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Select Vehicle</Label>
            <VehicleSliderPicker value={vehicle} onChange={handleVehicleChange} />
          </div>

          {/* Pickup & Dropoff */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Pickup */}
            <div className="space-y-4">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                Pick-up
              </Label>
              <Select value={pickupLocation} onValueChange={setPickupLocation}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.value} value={loc.value}>
                      {loc.label} {loc.price > 0 ? `- $${loc.price}` : '(Free)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DatePicker
                date={pickupDate}
                onDateChange={(newDate) => {
                  setPickupDate(newDate);
                  if (newDate && dropoffDate && dropoffDate <= newDate) {
                    const nextDay = new Date(newDate);
                    nextDay.setDate(nextDay.getDate() + 1);
                    setDropoffDate(nextDay);
                  }
                }}
                placeholder="Pick-up date"
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                data-testid="pickup-date"
              />
            </div>

            {/* Dropoff */}
            <div className="space-y-4">
              <Label className="text-xs font-semibold uppercase text-muted-foreground">
                Drop-off
              </Label>
              <Select value={dropoffLocation} onValueChange={setDropoffLocation}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.value} value={loc.value}>
                      {loc.label} {loc.price > 0 ? `- $${loc.price}` : '(Free)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DatePicker
                date={dropoffDate}
                onDateChange={setDropoffDate}
                placeholder="Drop-off date"
                disabled={(date) =>
                  date < new Date(new Date().setHours(0, 0, 0, 0)) ||
                  (pickupDate ? date < pickupDate : false)
                }
                data-testid="dropoff-date"
              />
            </div>
          </div>

          <Button type="submit" className="w-full" size="lg" disabled={!canProceed}>
            Continue Booking
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// Processing Step Component
function ProcessingStep({ label, delay }: { label: string; delay: number }) {
  const [visible, setVisible] = useState(false);
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), delay);
    const completeTimer = setTimeout(() => setCompleted(true), delay + 1200);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(completeTimer);
    };
  }, [delay]);

  if (!visible) return null;

  return (
    <div className={cn(
      "flex items-center gap-3 p-3 rounded-lg transition-all duration-500",
      completed ? "bg-green-500/10" : "bg-white/[0.05]"
    )}>
      {completed ? (
        <CheckCircle2 className="h-5 w-5 text-green-500" />
      ) : (
        <Loader2 className="h-5 w-5 text-primary animate-spin" />
      )}
      <span className={cn(
        "text-sm transition-colors",
        completed ? "text-green-500" : "text-foreground"
      )}>
        {label}
      </span>
    </div>
  );
}
